import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import { FC, useEffect, useState } from 'react'
import { MdOutlineSave } from 'react-icons/md'
import { suppliesApi, SupplyItemInput } from '../api'
import { useSelectedSeller } from '../hooks/useSelectedSeller'
import { useUserId } from '../hooks/useUserId'

type InsumoCode = 'caja' | 'bolsa_a' | 'bolsa_b' | 'sello'

export interface ActualizarStockTarget {
  idStore: number
  tienda: string
  stock: Record<InsumoCode, number>
}

const FIELDS: Array<[string, InsumoCode]> = [
  ['Cajas', 'caja'],
  ['Bolsas A', 'bolsa_a'],
  ['Bolsas B', 'bolsa_b'],
  ['Sellos', 'sello']
]

interface Props {
  target: ActualizarStockTarget | null
  onClose: () => void
  onSaved: () => void
}

const ActualizarStockModal: FC<Props> = ({ target, onClose, onSaved }) => {
  const idSeller = useSelectedSeller()
  const userId = useUserId()
  const isOpen = target !== null

  const [stock, setStock] = useState<Record<InsumoCode, number>>({ caja: 0, bolsa_a: 0, bolsa_b: 0, sello: 0 })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (target !== null) {
      setStock({ ...target.stock })
      setSaving(false)
    }
  }, [target])

  if (!isOpen || target === null) return null

  const onSubmit = async (): Promise<void> => {
    const items: SupplyItemInput[] = FIELDS.map(([, code]) => ({ codigo: code, cantidad: Math.max(0, stock[code]) }))
    setSaving(true)
    try {
      const r = await suppliesApi.reportInventory(target.idStore, idSeller, items, userId)
      const cambios = r.aplicados.filter(a => a.delta !== 0).length
      enqueueSnackbar(
        cambios > 0
          ? `Inventario de ${target.tienda} actualizado · ${cambios} cambio${cambios > 1 ? 's' : ''}`
          : `Inventario de ${target.tienda} confirmado (sin cambios)`,
        { variant: 'success' }
      )
      onSaved()
      onClose()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo actualizar el inventario', { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='md' backdrop='blur' placement='top-center'>
      <ModalContent>
        <ModalHeader>
          <div>
            <div className='text-[10px] uppercase tracking-widest text-[#37417f]/60 font-bold'>
              Actualizar inventario
            </div>
            <h3 className='text-lg font-bold text-[#151930] mt-0.5'>{target.tienda}</h3>
            <p className='text-xs text-[#151930]/60 mt-0.5'>
              Indica cuánto hay hoy físicamente en la tienda. Reemplaza el stock registrado.
            </p>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-2'>
            {FIELDS.map(([label, code]) => (
              <Input
                key={code}
                type='number'
                size='sm'
                label={label}
                value={String(stock[code])}
                onChange={(e) => setStock({ ...stock, [code]: Math.max(0, Number(e.target.value) || 0) })}
              />
            ))}
          </div>
        </ModalBody>

        <ModalFooter className='flex items-center justify-end gap-2'>
          <Button variant='bordered' onPress={onClose} isDisabled={saving} className='border-gray-200 text-[#151930]'>
            Cancelar
          </Button>
          <Button onPress={() => { void onSubmit() }} isLoading={saving} className='bg-[#151930] text-white' endContent={<MdOutlineSave />}>
            Guardar inventario
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ActualizarStockModal
